import appConfig from "AppConfig";
import { asyncStrategy, syncStrategy } from "DependencyAccessStrategies";
import type { DependencyOptions, SetterOptions } from "DependencyOptions";
import { type DependencyResult, type LoadedDependencyResult, getAvailableResult } from "DependencyResult";
import type {
  DependencyAccessMixedValue,
  DependencyAccessStrategy,
  DependencyArgument,
  DependencyVisitorContext,
  DependencyVisitorGetterOptions,
  DependencyVisitorOptions,
} from "DependencyVisitorContext";
import { getInterfaceName, isEntity } from "EntityExtensions";
import { DependencyError, DependencyOptionsError, addErrorData } from "Errors";
import { getFullText } from "ExpressionVisitorUtils";
import { type ParseTree, type ParserRuleContext, type RuleNode, TerminalNode } from "antlr4";
import ko from "knockout";
import { ExpressionParser, ExpressionVisitor } from "wtg-expressions";
import {
  type AdddaysContext,
  type AllContext,
  type AnyContext,
  type Arbitrary_funcContext,
  type Boolean_operatorContext,
  type ContainsContext,
  type CountContext,
  type DateContext,
  type DatetimeContext,
  type DependencyContext,
  type Dependency_pathContext,
  Embedded_pathContext,
  type EndswithContext,
  type ExprContext,
  type Expr_partContext,
  type FirstContext,
  Func_sequenceContext,
  type IndexofContext,
  type IsdefaultContext,
  type LengthContext,
  type Orderby_coreContext,
  type OriginalvalueContext,
  PathContext,
  Path_partContext,
  type SkipQContext,
  SlashSeparatorContext,
  type StartswithContext,
  type SubstringContext,
  type SumContext,
  type TakeContext,
  type TolowerContext,
  type ToupperContext,
  type TrimmillisecondsContext,
  type TrimsecondsContext,
  type Type_nameContext,
  type UrlencodeContext,
  type UtcdatetimeContext,
  type WhereContext,
} from "wtg-expressions/lib/ExpressionParser";
import booleanOperators from "./BooleanOperators.ts";
import collectionFunctions from "./CollectionFunctions.ts";
import dateFunctions from "./DateFunctions.ts";
import equalityOperators from "./EqualityOperators.ts";
import pathEvaluator from "./PathEvaluator.ts";
import polymorphicFunctions from "./PolymorphicFunctions.ts";
import stringFunctions from "./StringFunctions.ts";

export default class DependencyV2Visitor extends ExpressionVisitor<unknown> {
  private _dependencyType: DependencyType;
  private _state: VisitorState;

  constructor() {
    super();
    this._dependencyType = DependencyType.Other;
    this._state = getState(true);
  }

  static avoidLoading<T>(callback: () => T): T {
    try {
      avoidLoadingSemaphore++;
      return callback();
    } finally {
      avoidLoadingSemaphore--;
    }
  }

  static isValidDependency(dependency: string): boolean {
    return this.isValid(dependency, this.TreeName.Dependency);
  }

  static async visitAsync<T>(
    data: unknown,
    dependency: string,
    options?: DependencyVisitorGetterOptions,
  ): Promise<LoadedDependencyResult<T>>;
  static async visitAsync(data: unknown, dependency: string, options: SetterOptions): Promise<unknown>;
  static async visitAsync<T>(
    data: unknown,
    dependency: string,
    options?: DependencyVisitorOptions,
  ): Promise<LoadedDependencyResult<T>> {
    try {
      return (await this._visit(asyncStrategy, data, dependency, options)) as LoadedDependencyResult<T>;
    } catch (error) {
      setErrorData(error, data, dependency);
      throw error;
    }
  }

  static visitSync<T>(data: unknown, dependency: string, options?: DependencyVisitorGetterOptions): DependencyResult<T>;
  static visitSync(data: unknown, dependency: string, options: SetterOptions): void;
  static visitSync<T>(data: unknown, dependency: string, options?: DependencyVisitorOptions): DependencyResult<T> {
    try {
      return this._visit(syncStrategy, data, dependency, options) as DependencyResult<T>;
    } catch (error) {
      setErrorData(error, data, dependency);
      throw error;
    }
  }

  private static _visit<Mode>(
    strategy: DependencyAccessStrategy<Mode>,
    data: unknown,
    dependency: string,
    options?: DependencyOptions,
  ): DependencyAccessMixedValue<Mode, unknown> {
    let parsed = reducerCache.get(dependency);
    if (!parsed) {
      parsed = this._parseDependency(dependency);
      if (reducerCache.size + 1 > appConfig.dependencyVisitorCacheLimit) {
        reducerCache.clear();
      }
      reducerCache.set(dependency, parsed);
    }

    options = options || emptyOptions;
    this._validateOptions(strategy, options, parsed);
    if (!parsed.reducer) {
      return getAvailableResult(data);
    }

    data = ko.unwrap(data);
    return parsed.reducer(data, getContext(strategy, data, options));
  }

  private static _parseDependency(dependency: string): ParsedDependency {
    const visitor = new this();
    const tree = this.getDependencyTree(
      dependency,
      this.TreeName.Dependency,
      onDependencyError.bind(undefined, dependency),
    );

    visitor.visit(tree);
    const reducer = visitor._state.operands[0];
    const isSelfDependency = !reducer;
    return {
      dependencyType: isSelfDependency ? DependencyType.BindingPath : visitor._dependencyType,
      reducer,
    };
  }

  protected static _validateOptions<Mode>(
    strategy: DependencyAccessStrategy<Mode>,
    options: DependencyVisitorOptions,
    parsed: ParsedDependency,
  ): void {
    if (options.valueToSet !== undefined) {
      switch (parsed.dependencyType) {
        case DependencyType.MacroPath:
          throw new DependencyOptionsError("valueToSet cannot be specified for a macro path.");

        case DependencyType.PropertyPath:
          if (strategy !== asyncStrategy) {
            throw new DependencyOptionsError("valueToSet cannot be used with the sync strategy.");
          }
          break;

        default:
          throw new DependencyOptionsError(
            "valueToSet cannot be specified for a dependency that is not a property path.",
          );
      }

      if (options.skipLastSegmentEvaluation) {
        throw new DependencyOptionsError("skipLastSegmentEvaluation cannot be true if valueToSet is specified.");
      }
    } else if (options.skipLastSegmentEvaluation) {
      if (parsed.dependencyType === DependencyType.Other) {
        throw new DependencyOptionsError(
          "skipLastSegmentEvaluation cannot be true for a dependency that is not a binding path.",
        );
      }
    }
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitAdddays(ctx: AdddaysContext): void {
    this._visitFunction(ctx, dateFunctions.addDays, 1);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitAll(ctx: AllContext): void {
    this._visitFunction(ctx, collectionFunctions.all, 1);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitAny(ctx: AnyContext): void {
    this._visitFunction(ctx, collectionFunctions.any, 1);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  override visitBoolean_operator(ctx: Boolean_operatorContext): void {
    const state = this._state;
    switch (getChild<TerminalNode>(ctx, 0)?.symbol?.type) {
      case ExpressionParser.AND:
        state.operators.push(booleanOperators.and);
        break;
      case ExpressionParser.OR:
        state.operators.push(booleanOperators.or);
        break;
      default:
        throw new DependencyError(`Unknown boolean operator '${ctx.getText()}'.`);
    }
  }
  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitContains(ctx: ContainsContext): void {
    this._visitFunction(ctx, polymorphicFunctions.contains, 1);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  override visitDependency_path(ctx: Dependency_pathContext): void {
    this.visitChildren(ctx);

    const children = getChild<ParserRuleContext>(ctx, 0)?.children;
    if (children) {
      const firstChild = children[0];
      if (firstChild instanceof Path_partContext) {
        const lastChild = children[children.length - 1];
        if (isPropertyName(lastChild)) {
          if (firstChild.MACRO()) {
            this._dependencyType = DependencyType.MacroPath;
          } else {
            this._dependencyType = DependencyType.PropertyPath;
          }
        } else if (lastChild instanceof SlashSeparatorContext && isPropertyName(children[children.length - 2])) {
          this._dependencyType = DependencyType.BindingPath;
        }
      }
    }
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  override visitDependency_expr(ctx: DependencyContext): void {
    this.visitChildren(ctx);

    const state = this._state;
    state.operands[0] = booleanOperators.coalesce.bind(undefined, state.operands[0]);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitEndswith(ctx: EndswithContext): void {
    this._visitFunction(ctx, stringFunctions.endsWith, 1);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitEqualsOperator(): void {
    this._state.operators.push(equalityOperators.equal);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitExpr(ctx: ExprContext): void {
    const parentState = this._state;
    const state = (this._state = getState());
    this.visitChildren(ctx);

    // Apply binary operators not yet handled by visitExpr_part.
    while (state.operators.length) {
      const operator = state.operators.shift()!;
      state.operands.splice(0, 2, operator.bind(undefined, state.operands[0], state.operands[1]));
    }

    parentState.operands.push(...state.operands);
    this._state = parentState;
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  override visitExpr_part(ctx: Expr_partContext): void {
    this.visitChildren(ctx);

    const state = this._state;
    while (state.operators.length && !isLowPriorityOperator(state.operators[state.operators.length - 1])) {
      const operator = state.operators.pop()!;
      if (isUnaryOperator(operator)) {
        const itemIndex = state.operands.length - 1;
        state.operands[itemIndex] = operator.bind(undefined, state.operands[itemIndex]);
      } else {
        const itemIndex = state.operands.length - 2;
        state.operands.splice(
          itemIndex,
          2,
          operator.bind(undefined, state.operands[itemIndex], state.operands[itemIndex + 1]),
        );
      }
    }
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitFirst(ctx: FirstContext): void {
    this._visitFunction(ctx, collectionFunctions.first, 0);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitGreaterOperator(): void {
    this._state.operators.push(equalityOperators.greaterThan);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitGreaterOrEqualOperator(): void {
    this._state.operators.push(equalityOperators.greaterThanOrEqual);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitIndexof(ctx: IndexofContext): void {
    this._visitFunction(ctx, stringFunctions.indexOf, 1);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitIsdefault(ctx: IsdefaultContext): void {
    const propertyName = getPrecedingPropertyName(ctx, true);
    if (!propertyName) {
      throw new DependencyError("IsDefault() can only be specified after a property name.");
    }

    this._addFunction(pathEvaluator.isDefault, [propertyName]);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitCount(ctx: CountContext): void {
    this._visitFunction(ctx, collectionFunctions.count, 0);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitDate(ctx: DateContext): void {
    this._visitFunction(ctx, dateFunctions.date, 0);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitDatetime(ctx: DatetimeContext): void {
    this._visitFunction(ctx, dateFunctions.dateTime, 0);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitLength(ctx: LengthContext): void {
    this._visitFunction(ctx, stringFunctions.length, 0);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitLessOperator(): void {
    this._state.operators.push(equalityOperators.lessThan);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitLessOrEqualOperator(): void {
    this._state.operators.push(equalityOperators.lessThanOrEqual);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitNot(): void {
    this._state.operators.push(booleanOperators.not);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitNotEqualsOperator(): void {
    this._state.operators.push(equalityOperators.notEqual);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  override visitOrderby_core(ctx: Orderby_coreContext): void {
    const parentState = this._state;
    const clauses = [];

    let reducer;
    if (ctx.children) {
      for (let i = 0; i < ctx.children.length; i++) {
        const child = ctx.children[i];
        if (child instanceof PathContext) {
          if (reducer) {
            clauses.push({ reducer, isDescending: false });
          }
          this._state = getState();
          this.visit(child);
          reducer = this._state.operands[0];
        } else if (child instanceof TerminalNode && child.symbol.type === ExpressionParser.DESC) {
          clauses.push({ reducer, isDescending: true });
          reducer = undefined;
        }
      }
    }
    if (reducer) {
      clauses.push({ reducer, isDescending: false });
    }

    this._state = parentState;
    this._addFunction(collectionFunctions.orderBy, [clauses]);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitOriginalvalue(ctx: OriginalvalueContext): void {
    const propertyName = getPrecedingPropertyName(ctx, false);
    if (!propertyName) {
      throw new DependencyError("OriginalValue() can only be specified immediately after a property name.");
    }

    const state = this._state;
    state.operands[state.operands.length - 1] = pathEvaluator.originalValue.bind(undefined, propertyName);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitPath(ctx: PathContext): void {
    const parentState = this._state;
    const state = (this._state = getState(parentState.isTopLevel));
    this.visitChildren(ctx);

    if (isMacroPathPart(ctx.getChild(0))) {
      parentState.operands.push(pathEvaluator.macroPath.bind(undefined, getFullText(ctx), state.operands));
    } else {
      const isEmbeddedPath = ctx.parentCtx instanceof Embedded_pathContext;
      if (isEmbeddedPath) {
        parentState.operands.push(pathEvaluator.rootPath.bind(undefined, state.operands));
      } else if (state.operands.length === 1) {
        parentState.operands.push(state.operands[0]);
      } else {
        parentState.operands.push(pathEvaluator.path.bind(undefined, state.operands) as DependencyArgument);
      }
    }

    this._state = parentState;
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  override visitPath_part(ctx: Path_partContext): void {
    if (ctx.MACRO()) {
      if (ctx.parentCtx?.getChild(0) !== ctx) {
        throw new DependencyError("Macro symbol can only be the first property in a path.");
      }
      return;
    }

    const { children } = ctx;
    if (!children) {
      throw new DependencyError("Expected context to have children.");
    }

    let nextCtx;
    if (children.length > 1) {
      nextCtx = children[1];
    } else {
      const parentChildren = ctx.parentCtx?.children;
      nextCtx = parentChildren?.[parentChildren.indexOf(ctx) + 1];
    }

    const propertyName = children[0].getText();
    const separator = nextCtx && nextCtx.getText();
    const state = this._state;
    state.operands.push(this._getPropertyValueFunction().bind(undefined, propertyName, separator, state.isTopLevel));

    this.visitChildren(ctx);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitSkipQ(ctx: SkipQContext): void {
    this._visitFunction(ctx, collectionFunctions.skip, 1);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitStartswith(ctx: StartswithContext): void {
    this._visitFunction(ctx, stringFunctions.startsWith, 1);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitSubstring(ctx: SubstringContext): void {
    this._visitFunction(ctx, stringFunctions.substring, 2);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitSum(ctx: SumContext): void {
    this._visitFunction(ctx, collectionFunctions.sum, 1);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitTake(ctx: TakeContext): void {
    this._visitFunction(ctx, collectionFunctions.take, 1);
  }

  override visitTerminal(node: TerminalNode): void {
    const evaluator = terminalEvaluator[node.symbol.type];
    if (evaluator) {
      this._state.operands.push(evaluator(node));
    }
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitTolower(ctx: TolowerContext): void {
    this._visitFunction(ctx, stringFunctions.toLower, 0);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitToupper(ctx: ToupperContext): void {
    this._visitFunction(ctx, stringFunctions.toUpper, 0);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitTrimmilliseconds(ctx: TrimmillisecondsContext): void {
    this._visitFunction(ctx, dateFunctions.trimMilliseconds, 0);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitTrimseconds(ctx: TrimsecondsContext): void {
    this._visitFunction(ctx, dateFunctions.trimSeconds, 0);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  override visitType_name(ctx: Type_nameContext): void {
    this._state.operands.push(pathEvaluator.ofType.bind(undefined, ctx.getText()));
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitUrlencode(ctx: UrlencodeContext): void {
    this._visitFunction(ctx, stringFunctions.urlEncode, 0);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitUtcdatetime(ctx: UtcdatetimeContext): void {
    this._visitFunction(ctx, dateFunctions.utcDateTime, 0);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  override visitWhere(ctx: WhereContext): void {
    this._visitFunction(ctx, collectionFunctions.where, 1);
  }

  // @ts-expect-error Use prototype member instead of instance function for performance.
  //                  Remove suppression when https://github.com/antlr/antlr4/issues/4721 is resolved.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  override visitArbitrary_func(ctx: Arbitrary_funcContext): void {
    const fnName = ctx.getChild(0)?.getText();
    throw new DependencyError(`Function '${fnName}' is not supported in this context.`);
  }

  private _addFunction(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fn: (...args: any[]) => unknown,
    args: unknown[],
    hasFreeParams = false,
  ): void {
    const state = this._state;
    let index = state.operands.length;
    let previousOperand;

    if (index) {
      index--;
      previousOperand = state.operands[index];
    } else {
      previousOperand = identity;
    }

    state.operands[index] = (
      hasFreeParams ? fn.bind(undefined, previousOperand, args) : fn.bind(undefined, previousOperand, ...args)
    ) as DependencyArgument;
  }

  protected _getPropertyValueFunction<Mode>(): PropertyValueFunction<Mode> {
    return pathEvaluator.property;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected _visitFunction(ctx: RuleNode, fn: (...args: any[]) => unknown, argCount: number | undefined): void {
    let args: DependencyArgument[];
    const hasFreeParams = argCount === undefined;
    if (hasFreeParams || argCount > 0) {
      const parentState = this._state;
      const state = (this._state = getState());
      this.visitChildren(ctx);
      args = state.operands;
      if (!hasFreeParams && args.length !== argCount) {
        args.length = argCount;
      }
      this._state = parentState;
    } else {
      args = [];
    }

    this._addFunction(fn, args, hasFreeParams);
  }
}

let avoidLoadingSemaphore = 0;

const reducerCache = new Map<string, ParsedDependency>();
const emptyOptions = Object.freeze({});

const nullLiteral = literal.bind(undefined, null);
const falseLiteral = literal.bind(undefined, false);
const trueLiteral = literal.bind(undefined, true);

const terminalEvaluator: Record<
  number,
  (ctx: TerminalNode) => <Mode>(data: unknown, context: DependencyVisitorContext<Mode>) => DependencyResult
> = {
  [ExpressionParser.FALSE]() {
    return falseLiteral;
  },

  [ExpressionParser.FLOAT](ctx) {
    return literal.bind(undefined, parseFloat(ctx.getText()));
  },

  [ExpressionParser.INT](ctx) {
    return literal.bind(undefined, parseInt(ctx.getText()));
  },

  [ExpressionParser.NULL]() {
    return nullLiteral;
  },

  [ExpressionParser.PARAM](ctx) {
    const name = ctx.getText();
    const index = parseInt(name.substring(2)) - 1;
    if (isNaN(index) || index < 0) {
      throw new DependencyError(`Parameter '${name}' is not in a supported format.`);
    }
    return param.bind(undefined, index);
  },

  [ExpressionParser.QUOTED_STRING](ctx) {
    return literal.bind(undefined, unquoteString(ctx.getText()));
  },

  [ExpressionParser.TRUE]() {
    return trueLiteral;
  },
};

export function clearReducerCache(): void {
  reducerCache.clear();
}

function getChild<T extends ParseTree>(ctx: ParserRuleContext, index: number): Partial<T> | undefined {
  return ctx.getChild(index) as Partial<T> | undefined;
}

function getContext<Mode>(
  strategy: DependencyAccessStrategy<Mode>,
  data: unknown,
  options: DependencyVisitorOptions,
): DependencyVisitorContext<Mode> {
  return {
    options,
    rootData: data,
    shouldAvoidLoading,
    strategy,
  };
}

function getPrecedingPropertyName(ctx: ParserRuleContext, allowPrecedingFunction: boolean): string | undefined {
  while (ctx.parentCtx) {
    const { parentCtx } = ctx;
    if (parentCtx instanceof Func_sequenceContext) {
      if (!allowPrecedingFunction && parentCtx.getChild(0) !== ctx) {
        break;
      }
    } else if (parentCtx instanceof Path_partContext) {
      const { children } = parentCtx;
      if (children?.indexOf(ctx) === 2) {
        return children[0].getText();
      }
      break;
    } else if (parentCtx instanceof PathContext) {
      break;
    }
    ctx = parentCtx;
  }

  return undefined;
}

function getState(isTopLevel = false): VisitorState {
  return {
    isTopLevel,
    operands: [],
    operators: [],
  };
}

function identity(data: unknown): DependencyResult {
  return getAvailableResult(data);
}

function isLowPriorityOperator(operator: unknown): boolean {
  return operator === booleanOperators.or;
}

function isMacroPathPart(ctx: ParseTree | undefined): boolean {
  return ctx instanceof Path_partContext && ctx.MACRO() != null;
}

function isPropertyName(ctx: ParseTree): boolean {
  return ctx instanceof Path_partContext && ctx.children?.length === 1;
}

function isUnaryOperator(operator: unknown): boolean {
  return operator === booleanOperators.not;
}

function literal(value: unknown): DependencyResult {
  return getAvailableResult(value);
}

function onDependencyError(expression: string, message: string): never {
  throw new DependencyError(`Invalid expression '${expression}'. Error: ${message}`);
}

function param<Mode>(index: number, _data: unknown, context: DependencyVisitorContext<Mode>): DependencyResult {
  const { params } = context.options;
  if (!params || index >= params.length) {
    throw new DependencyError(
      `Invalid parameter access. Requested position: ${index + 1}, available count: ${params ? params.length : 0}`,
    );
  }
  return getAvailableResult(params[index]);
}

function setErrorData(error: unknown, data: unknown, dependency: string): void {
  if (error instanceof Error) {
    data = ko.unwrap(data);
    addErrorData(error, [
      { name: "Dependency", value: dependency },
      {
        name: "EntityType",
        value: isEntity(data) ? getInterfaceName(data) : null,
      },
    ]);
  }
}

function shouldAvoidLoading(): boolean {
  return avoidLoadingSemaphore > 0;
}

function unquoteString(text: string): string {
  return text.substring(1, text.length - 1);
}

export enum DependencyType {
  Other,
  BindingPath,
  PropertyPath,
  MacroPath,
}

export interface ParsedDependency {
  dependencyType: DependencyType;
  reducer: DependencyArgument;
}

export interface PropertyValueFunction<Mode> {
  (
    propertyName: string,
    separator: string | undefined,
    isTopLevel: boolean,
    data: unknown,
    context: DependencyVisitorContext<Mode>,
  ): DependencyAccessMixedValue<Mode, unknown>;
}

interface VisitorState {
  isTopLevel: boolean;
  operands: DependencyArgument[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  operators: (<Mode>(...args: any[]) => DependencyAccessMixedValue<Mode, unknown>)[];
}
